import { Pencil, People } from '@factorialco/f0-react/icons/app'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import type { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'
import {
  getPeriodStatusLabel,
  PLANNING_PERIOD_STATUS_COLORS,
} from 'modules/planning/consts/period_status'
import {
  getRequestStatusLabel,
  REQUEST_STATUS_COLORS,
} from 'modules/planning/consts/request_status'
import {
  formatPeriod,
  getRequestDeadline,
  getRequestStatus,
  isPeriodEditable,
} from 'modules/planning/lib/periodHelpers'
import type { PlanningPeriodDetailQuery } from 'generated/resources/graphql'
import type { PlanningCapability } from 'modules/planning/roles/capabilities'

export type PeriodDetailData = NonNullable<PlanningPeriodDetailQuery['planning']['period']>

export function buildResourceHeaderProps(
  period: PeriodDetailData,
  capabilities: Set<PlanningCapability>
): ResourceHeadersPropsWithActions {
  const requestStatus = getRequestStatus(period)
  const isLocked = !isPeriodEditable(period.status)

  const secondaryActions = [
    ...(capabilities.has('editPeriod')
      ? [{ key: 'edit-period', label: i18n.t('planning.period_detail.edit'), icon: Pencil }]
      : []),
    ...(capabilities.has('manageCollaborators')
      ? [
          {
            key: 'manage-collaborators',
            label: i18n.t('planning.manage_collaborators.action_label'),
            icon: People,
          },
        ]
      : []),
  ]

  return {
    title: period.name,
    secondaryActions,
    status: {
      label: i18n.t('planning.period_detail.status'),
      text: getPeriodStatusLabel(period.status),
      variant: PLANNING_PERIOD_STATUS_COLORS[period.status],
    },
    metadata: [
      {
        label: i18n.t('planning.period_detail.period'),
        value: {
          type: 'text' as const,
          content: formatPeriod(period),
        },
      },
      {
        label: i18n.t('planning.period_detail.request_deadline'),
        value: {
          type: 'text' as const,
          content: getRequestDeadline(period) ?? '-',
        },
      },
      ...(capabilities.has('viewCollaborators') && period.participantsConnection.edges.length > 0
        ? [
            {
              label: i18n.t('planning.period_detail.collaborators'),
              value: {
                avatars: period.participantsConnection.edges.map((edge) => ({
                  type: 'person' as const,
                  firstName: edge.node.employee.firstName,
                  lastName: edge.node.employee.lastName,
                  src: buildUrl(edge.node.employee.avatar?.url),
                })),
                type: 'list' as const,
                variant: 'person' as const,
              },
            },
          ]
        : []),
      ...(capabilities.has('viewPlanners') && period.plannersConnection.edges.length > 0
        ? [
            {
              label: i18n.t('planning.period_detail.headcount_planners'),
              value: {
                avatars: period.plannersConnection.edges.map((edge) => ({
                  type: 'person' as const,
                  firstName: edge.node.employee.firstName,
                  lastName: edge.node.employee.lastName,
                  src: buildUrl(edge.node.employee.avatar?.url),
                })),
                type: 'list' as const,
                variant: 'person' as const,
              },
            },
          ]
        : []),
    ],
    ...(capabilities.has('manageSubmissionPeriods') && !isLocked
      ? {
          primaryAction: {
            key: 'submission-period',
            label: period.submissionPeriods?.[0]
              ? i18n.t('planning.submission_period.edit_button')
              : i18n.t('planning.submission_period.create_button'),
          },
        }
      : {}),
  }
}
